import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, XCircle, User, Image, X } from 'lucide-react';

const SecondCheckPage = () => {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQC, setSelectedQC] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [action, setAction] = useState('');
  const [notes, setNotes] = useState('');
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [photoQC, setPhotoQC] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);

  // Fetch photos on demand
  const handleViewPhotos = async (qc) => {
    setPhotoLoading(true);
    setShowPhotoDialog(true);
    try {
      const response = await api.get(`/qc/${qc.id}`);
      setPhotoQC(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      toast.error('Failed to load photos');
      setPhotoQC(qc);
    } finally {
      setPhotoLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await api.get('/second-check');
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch second check tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    try {
      await api.post(`/second-check/${selectedQC.id}`, { action, notes });
      toast.success(`QC ${action.toLowerCase()} successfully!`);
      setShowDialog(false);
      setNotes('');
      fetchTasks();
    } catch (error) {
      console.error('Failed to perform second check:', error);
      toast.error(error.response?.data?.detail || 'Failed to perform action');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="second-check-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Second Check Queue</h1>
        <p className="text-lg text-muted-foreground">{tasks.length} pending tasks</p>
      </div>

      {tasks.length === 0 ? (
        <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
          <p className="text-xl text-muted-foreground">No pending second checks</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {tasks.map((qc) => (
            <div key={qc.id} data-testid={`second-check-task-${qc.id}`} className="bg-white rounded-xl border border-border shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-3">{qc.asset_id}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <p>
                      <strong>Customer:</strong> {qc.customer_or_company_name}
                    </p>
                    <p>
                      <strong>Product:</strong> {qc.product_category}
                    </p>
                    <p>
                      <strong>Created By:</strong> {qc.created_by_technician}
                    </p>
                    <p>
                      <strong>QC Status:</strong>{' '}
                      <span
                        className={`px-2 py-1 rounded text-xs font-bold ${
                          qc.qc_status === 'PASS' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {qc.qc_status}
                      </span>
                    </p>
                    <p>
                      <strong>Score:</strong> {qc.qc_score}/100
                    </p>
                    <p>
                      <strong>Created:</strong> {new Date(qc.created_timestamp).toLocaleDateString()}
                    </p>
                    {isAdmin && qc.second_check_technician && (
                      <p className="col-span-2 mt-2">
                        <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          <User className="w-4 h-4 mr-1" />
                          Assigned to: {qc.second_check_technician}
                        </span>
                      </p>
                    )}
                  </div>
                  {qc.hold_reasons && qc.hold_reasons.length > 0 && (
                    <div className="mt-3">
                      <p className="font-bold text-destructive text-sm">Hold Reasons: {qc.hold_reasons.join(', ')}</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-col space-y-2">
                  <Button
                    onClick={() => handleViewPhotos(qc)}
                    variant="outline"
                    data-testid={`view-photos-${qc.id}`}
                  >
                    <Image className="w-4 h-4 mr-2" />
                    View Photos
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedQC(qc);
                      setAction('Approved');
                      setShowDialog(true);
                    }}
                    data-testid={`approve-button-${qc.id}`}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedQC(qc);
                      setAction('Rejected');
                      setShowDialog(true);
                    }}
                    data-testid={`reject-button-${qc.id}`}
                    variant="destructive"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{action} QC Check</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Are you sure you want to {action.toLowerCase()} this QC check?</p>
            <div>
              <label className="block text-sm font-medium mb-2">Notes (optional)</label>
              <Textarea
                data-testid="second-check-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes..."
                className="min-h-[100px]"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAction} data-testid="confirm-action-button">
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Viewing Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>QC Photos - {photoQC?.asset_id}</span>
              <span className="text-sm font-normal text-muted-foreground">
                {photoQC?.photos?.length || 0} photo(s)
              </span>
            </DialogTitle>
          </DialogHeader>
          {photoLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">Loading photos...</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {photoQC?.photos?.map((photo, idx) => (
                  <div key={idx} className="relative border rounded-lg overflow-hidden">
                    <img
                      src={photo.data}
                      alt={`QC Photo ${idx + 1}`}
                      className="w-full h-auto object-contain max-h-80"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2">
                      <p><strong>Taken:</strong> {photo.formatted_timestamp || photo.timestamp || 'N/A'}</p>
                      <p><strong>Category:</strong> {photo.category || 'General'}</p>
                    </div>
                  </div>
                ))}
              </div>
              {(!photoQC?.photos || photoQC.photos.length === 0) && (
                <p className="text-center text-muted-foreground py-8">No photos available for this QC entry</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecondCheckPage;